import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "amount", "includeFee", "feeLabel", "total", "form", "formContainer", "errors",
    "fullName", "email", "telephone", "contributeTo",
    "step1", "step2", "continueButton", "submitButton",
    "paymentElement", "paymentTotal", "frequencySuffix", "paymentFrequencySuffix",
    "locale", "csrfToken"
  ]

  static values = {
    publishableKey: String,
    successUrl: String,
    createUrl: String,
    feeLabel: String
  }

  connect() {
    this.stripe = null
    this.elements = null
    this.paymentElement = null
    this.clientSecret = null
    this.donationId = null
    this.frequency = "one_time"
    this.recalculate()
  }

  recalculate() {
    const amount = parseFloat(this.amountTarget.value) || 0
    const fee = this.calculateFee(amount)

    if (fee > 0) {
      const suffix = this.frequencySuffixText()
      this.feeLabelTarget.textContent = this.feeLabelValue.replace("{fee}", `$${fee.toFixed(2)}`) + suffix
    }

    let total = amount
    if (this.includeFeeTarget.checked) {
      total += fee
    }

    this.totalTarget.textContent = total > 0 ? total.toFixed(2) : "0.00"
  }

  calculateFee(amount) {
    const flatFee = 0.30
    const discountFee = this.calcDiscountFee(amount + flatFee)
    return discountFee + flatFee
  }

  calcDiscountFee(amount) {
    if (amount.toFixed(2) > 0.0) {
      const fee = amount * 0.029
      return fee + this.calcDiscountFee(fee)
    }
    return 0.0
  }

  formatCurrency(event) {
    const amount = parseFloat(event.target.value)
    if (!isNaN(amount)) {
      event.target.value = amount.toFixed(2)
    }
  }

  frequencyChanged(event) {
    this.frequency = event.target.value
    const suffix = this.frequencySuffixText()
    this.frequencySuffixTarget.textContent = suffix
    if (this.hasPaymentFrequencySuffixTarget) {
      this.paymentFrequencySuffixTarget.textContent = suffix
    }
    this.recalculate()
  }

  frequencySuffixText() {
    if (this.frequency === "weekly") return "/week"
    if (this.frequency === "monthly") return "/month"
    return ""
  }

  async continueToPayment() {
    if (!this.validate()) return

    this.continueButtonTarget.disabled = true
    this.continueButtonTarget.textContent = "Processing..."

    try {
      const total = parseFloat(this.totalTarget.textContent) || 0
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfTokenTarget.value
        },
        body: JSON.stringify({
          donation: {
            full_name: this.fullNameTarget.value.trim(),
            email: this.emailTarget.value.trim(),
            telephone: this.telephoneTarget.value.trim(),
            amount: parseFloat(this.amountTarget.value),
            total: total,
            frequency: this.frequency,
            contribute_to: this.contributeToTarget.value,
            include_service_fee: this.includeFeeTarget.checked,
            locale: this.localeTarget.value
          }
        })
      })

      const data = await response.json()

      if (!response.ok) {
        this.showError(data.errors.join(", "))
        this.continueButtonTarget.disabled = false
        this.continueButtonTarget.textContent = "Continue to Payment"
        return
      }

      this.clientSecret = data.client_secret
      this.donationId = data.donation_id

      // Initialize Stripe and mount Payment Element
      if (!this.stripe) {
        this.stripe = Stripe(this.publishableKeyValue)
      }

      this.elements = this.stripe.elements({ clientSecret: this.clientSecret })
      this.paymentElement = this.elements.create("payment")
      this.paymentElement.mount(this.paymentElementTarget)

      // Update payment summary
      this.paymentTotalTarget.textContent = total.toFixed(2)
      this.paymentFrequencySuffixTarget.textContent = this.frequencySuffixText()

      // Show step 2
      this.step1Target.classList.add("hidden")
      this.step2Target.classList.remove("hidden")
      this.hideError()
    } catch (error) {
      this.showError("Something went wrong. Please try again.")
      this.continueButtonTarget.disabled = false
      this.continueButtonTarget.textContent = "Continue to Payment"
    }
  }

  backToDetails() {
    this.step2Target.classList.add("hidden")
    this.step1Target.classList.remove("hidden")
    this.continueButtonTarget.disabled = false
    this.continueButtonTarget.textContent = "Continue to Payment"

    // Unmount payment element to avoid stale state
    if (this.paymentElement) {
      this.paymentElement.unmount()
      this.paymentElement = null
    }
  }

  async submitPayment() {
    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.textContent = "Processing..."
    this.hideError()

    const successUrl = new URL(this.successUrlValue)
    successUrl.searchParams.set("donation_id", this.donationId)

    const { error } = await this.stripe.confirmPayment({
      elements: this.elements,
      confirmParams: {
        return_url: successUrl.toString()
      }
    })

    if (error) {
      this.showError(error.message)
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.textContent = "Donate"
    }
  }

  validate() {
    const errors = []
    if (!this.fullNameTarget.value.trim()) errors.push("Full name is required")
    if (!this.emailTarget.value.trim()) errors.push("Email is required")
    if (!this.telephoneTarget.value.trim()) errors.push("Telephone is required")

    const amount = parseFloat(this.amountTarget.value)
    if (!amount || amount <= 0) errors.push("Amount must be greater than 0")

    if (errors.length > 0) {
      this.showError(errors.join(", "))
      return false
    }
    this.hideError()
    return true
  }

  showError(message) {
    this.errorsTarget.textContent = message
    this.errorsTarget.classList.remove("hidden")
  }

  hideError() {
    this.errorsTarget.classList.add("hidden")
  }
}
